import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn } from '../luna-common';
import { brainMark } from '../luna-logo/brain-mark';

type TopBarVariant = 'home' | 'branded';

const VARIANTS: readonly TopBarVariant[] = ['home', 'branded'];

@customElement('luna-top-bar')
export class LunaTopBar extends LitPage {
  private _variant: TopBarVariant = 'home';

  @property({ attribute: 'variant' })
  get variant(): TopBarVariant {
    return this._variant;
  }

  set variant(value: string | null) {
    const next = String(value ?? '');
    if (!(VARIANTS as readonly string[]).includes(next)) {
      devWarn(`luna-top-bar: unknown variant "${next}" — falling back to "home"`);
      this._variant = 'home';
      return;
    }
    this._variant = next as TopBarVariant;
  }

  content() {
    /* BR-104: the logo lockup is on EVERY screen; slotted chrome (back chip)
       sits to the RIGHT of the logo. No title text in the bar. */
    return html`
      <header class="luna-top-bar luna-top-bar--${this._variant}">
        ${this.logoLockup()}
        <div class="luna-top-bar__lead"><slot name="lead"></slot></div>
        <div class="luna-top-bar__trail"><slot name="trail"></slot></div>
      </header>
    `;
  }

  private logoLockup() {
    const branded = this._variant === 'branded';
    return html`
      <span class="luna-top-bar__lockup ${branded ? 'luna-top-bar__lockup--branded' : ''}">
        ${brainMark('luna-top-bar')}
        <span class="luna-top-bar__wordmark ${branded ? 'mbw-type-label-button-xl' : 'mbw-type-title-topbar'}">
          <span class="luna-top-bar__word">MyBrain</span
          ><span class="luna-top-bar__word luna-top-bar__word--accent">Works</span>
        </span>
      </span>
    `;
  }

  styles() {
    return css`
      .luna-top-bar {
        display: flex;
        align-items: center;
        gap: 16px;
        width: 100%;
        height: var(--mbw-size-topbar-h);
        box-sizing: border-box;
        color: var(--mbw-onbar-text);
        box-shadow: inset 0 1px 0 #ffffff, 0 2px 8px rgba(10, 40, 120, 0.35); /* pinned: figma Luna Top Bar (#0a2878) */
      }

      .luna-top-bar--home {
        padding: 0 30px;
      }

      /* Home has no back chip: collapse the empty lead slot wrapper so the
         lockup sits at the padding edge (figma Home screens). */
      .luna-top-bar--home .luna-top-bar__lead {
        display: none;
      }

      .luna-top-bar--branded {
        padding: 0 22px;
      }

      .luna-top-bar__lead,
      .luna-top-bar__trail {
        display: flex;
        align-items: center;
      }

      .luna-top-bar__trail {
        margin-left: auto;
      }

      .luna-top-bar__lockup {
        display: inline-flex;
        align-items: center;
        gap: 12px;
      }

      /* Brain glyph: white left hemisphere, accent right hemisphere, neural
         network in the theme's signature colour — all theme-token driven. */
      .luna-top-bar__brain {
        display: block;
        flex: none;
        height: 42px;
        width: auto;
      }

      .luna-top-bar__lockup--branded .luna-top-bar__brain {
        height: 34px;
      }

      .luna-top-bar__brain-half--left {
        fill: var(--mbw-onbar-text);
      }

      .luna-top-bar__brain-half--right {
        fill: var(--mbw-onbar-accent);
      }

      .luna-top-bar__brain-line {
        fill: none;
        stroke: var(--mbw-topbar-solid);
      }

      .luna-top-bar__brain-node {
        fill: var(--mbw-topbar-solid);
      }

      .luna-top-bar__word--accent {
        color: var(--mbw-onbar-accent);
      }

      /* Per-theme top-bar gradient maps (register 6.2 — token layer only has the
         solid mid-stop, so the full gradient is pinned per theme). */
      [data-theme='blue'] .luna-top-bar {
        background-image: linear-gradient(180deg, #4a9bff 0%, #1358d8 14%, #0a46c4 60%, #0d51d6 100%); /* pinned: figma Luna Top Bar */
      }

      [data-theme='sand'] .luna-top-bar {
        background-image: linear-gradient(180deg, #7a6c5e 0%, #635546 55%, #4f4339 100%); /* pinned: figma Luna Top Bar */
      }

      [data-theme='slate'] .luna-top-bar {
        background-image: linear-gradient(180deg, #5a6068 0%, #41474e 16%, #2f343a 60%, #3a4046 100%); /* pinned: figma Luna Top Bar */
      }

      @media (max-width: 767px) {
        .luna-top-bar--home {
          padding: 0 12px 0 16px; /* pinned: figma Home mobile */
        }

        .luna-top-bar--home .luna-top-bar__brain {
          height: 34px;
        }
      }
    `;
  }
}
